/**
 * Protected Route Component
 * Redirects unauthenticated users to auth page
 */

import { Navigate, useLocation } from 'react-router';
import { useAuth } from '../lib/auth-context';
import { LoadingFallback } from './LoadingFallback';
import { trackTelemetryEvent } from '../lib/telemetry';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function isJwtExpired(token: string | null): boolean {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1] || ''));
    return typeof payload?.exp === 'number' ? payload.exp < Date.now() / 1000 : true;
  } catch {
    return true;
  }
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, accessToken, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingFallback fullScreen />;
  }

  const tokenExpired = isJwtExpired(accessToken);
  if (!user || tokenExpired) {
    const reason = !user ? 'missing_user' : 'invalid_or_expired_token';
    console.warn('Protected route redirect', { path: location.pathname, reason });
    void trackTelemetryEvent('auth_guard_redirected', {
      path: location.pathname,
      reason,
    });
    return (
      <Navigate
        to="/auth"
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  return <>{children}</>;
}
